import IwanClient, { type IwanClientOptions } from '@wandevs/iwan-sdk'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type IwanConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'error'
  | 'unconfigured'

interface WebSocketContextType {
  client: IwanClient | null
  status: IwanConnectionStatus
  error: Error | null
  call: <T = any>(method: string, params?: unknown) => Promise<T>
}

const WebSocketContext = createContext<WebSocketContextType | null>(null)

const getIwanClientOptions = (): IwanClientOptions => {
  const chainId = process.env.REACT_APP_CHAINID || '999'

  return {
    isTestnet: chainId !== '888',
  }
}

const getIwanCredentials = () => {
  return {
    apiKey: process.env.REACT_APP_API_KEY || '',
    secretKey:
      process.env.REACT_APP_API_SECRET ||
      process.env.REACT_APP_SECRET_KEY ||
      '',
  }
}

const toError = (error: unknown) => {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)
  return new Error('Unknown iWan SDK error')
}

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [client, setClient] = useState<IwanClient | null>(null)
  const [status, setStatus] = useState<IwanConnectionStatus>('idle')
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const { apiKey, secretKey } = getIwanCredentials()

    if (!apiKey || !secretKey) {
      setStatus('unconfigured')
      setError(new Error('Missing REACT_APP_API_KEY or REACT_APP_API_SECRET'))
      setClient(null)
      return
    }

    let disposed = false
    setStatus('connecting')
    setError(null)

    const iwanClient = new IwanClient(apiKey, secretKey, getIwanClientOptions())

    iwanClient.on('open', () => {
      if (!disposed) {
        setStatus('open')
        setError(null)
      }
    })
    iwanClient.on('reconnect', () => {
      if (!disposed) setStatus('reconnecting')
    })
    iwanClient.on('close', () => {
      if (!disposed) setStatus('closed')
    })
    iwanClient.on('closed', () => {
      if (!disposed) setStatus('closed')
    })
    iwanClient.on('error', (event) => {
      if (!disposed) {
        setStatus('error')
        setError(toError(event))
      }
    })

    setClient(iwanClient)

    return () => {
      disposed = true
      setClient(null)
      iwanClient.close().catch(() => {
        // Ignore shutdown errors during React unmount.
      })
    }
  }, [])

  const call = useCallback(
    async <T = any,>(method: string, params: unknown = {}) => {
      if (!client) {
        throw error || new Error('iWan SDK client is not ready')
      }

      return client.call<T>(method, params)
    },
    [client, error],
  )

  const contextValue = useMemo(
    () => ({
      client,
      status,
      error,
      call,
    }),
    [client, status, error, call],
  )

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider',
    )
  }
  return context
}
